import {getDuration} from '../utils/utils';

import {getReleaseStage, isShopJsError} from './utils';
import {BUGSNAG_NOTIFIER, RELEASE_STAGES} from './constants';
import {
  Breadcrumb,
  BreadcrumbType,
  BugsnagEventType,
  BugsnagRequestBody,
  ClientInitParams,
  BeforeNotifyCallback,
  ReleaseStage,
} from './types';
import BugsnagEvent from './BugsnagEvent';

export default class Client {
  private readonly apiKey: string;
  private readonly appId: string;
  private readonly appVersion: string;
  private readonly metadata: any;
  private readonly onError: BeforeNotifyCallback[] = [];
  private readonly releaseStage: ReleaseStage;
  private readonly startTime: Date;
  private breadcrumbs: Breadcrumb[] = [];

  constructor({
    apiKey,
    appId,
    appVersion,
    metadata,
    onError,
  }: ClientInitParams) {
    this.apiKey = apiKey;
    this.appId = appId;
    this.appVersion = appVersion;
    this.metadata = metadata;
    this.onError = onError || [];
    this.startTime = new Date();
    this.releaseStage = getReleaseStage();
    this.initWindowErrorHandler();
    this.leaveBreadcrumb('Bugsnag started', {}, 'state');
  }

  // eslint-disable-next-line no-warning-comments
  // TODO: Capture console.log output, Network requests
  leaveBreadcrumb(name: string, metaData: any, type: BreadcrumbType) {
    this.breadcrumbs.push({
      name,
      metaData,
      type,
      timestamp: new Date().toISOString(),
    });
  }

  notify(error: Error, onBeforeNotify?: BeforeNotifyCallback) {
    if (this.releaseStage === RELEASE_STAGES.dev) {
      this.logToConsole(error);
      return;
    }

    this.sendToBugsnag(error, onBeforeNotify);
  }

  private initWindowErrorHandler() {
    const prevOnError = window.onerror;
    window.onerror = (messageOrEvent, url, lineNo, charNo, error) => {
      if (error && isShopJsError(error)) {
        this.notify(error);
      }

      if (typeof prevOnError === 'function')
        prevOnError.apply(this, [messageOrEvent, url, lineNo, charNo, error]);
    };
  }

  private logToConsole(error: Error) {
    // eslint-disable-next-line no-console
    console.error(`[bugsnag - shop-js] error in ${this.releaseStage}`, {
      error,
    });
  }

  private sendToBugsnag(error: Error, onBeforeNotify?: BeforeNotifyCallback) {
    const now = new Date();
    const time = now.toISOString();
    const duration = getDuration(this.startTime, now);
    const {breadcrumbs, appId, appVersion, releaseStage, metadata} = this;

    const event: BugsnagEventType = new BugsnagEvent({
      error,
      breadcrumbs,
      appId,
      appVersion,
      releaseStage,
      duration,
      time,
      metaData: {...metadata},
    });

    // Combine global callback with the one passed to notify()
    const callbacks: BeforeNotifyCallback[] = [
      ...this.onError,
      ...(onBeforeNotify ? [onBeforeNotify] : []),
    ];

    if (callbacks) {
      const shouldNotNotify = callbacks.some((fn) => fn(event) === false);
      if (shouldNotNotify) return;
    }

    const request = new XMLHttpRequest();

    request.open('POST', 'https://notify.bugsnag.com/');

    request.setRequestHeader('Content-Type', 'application/json');
    request.setRequestHeader('Bugsnag-Api-Key', this.apiKey);
    request.setRequestHeader('Bugsnag-Payload-Version', '4');
    request.setRequestHeader('Bugsnag-Sent-At', time);

    const body: BugsnagRequestBody = {
      apiKey: this.apiKey,
      notifier: BUGSNAG_NOTIFIER,
      events: [event],
    };

    request.send(JSON.stringify(body));
  }
}
